import type { Project } from '@/storage/ProjectRepository';
import type { PlanRules } from './plan/planTypes';
import { listStartingRows } from './gate';
import { DATE_TOKEN } from './dateToken';
import {
  publishedSchemas, withAppRules, withManaged, withCounts, submitSchemas,
  type PublishedSchema,
} from './schemasFromSource';

// Re-exported from their old home so existing callers and tests are unaffected by where
// they actually live now.
export {
  publishedSchemas, withAppRules, withManaged, withCounts, submitSchemas,
} from './schemasFromSource';
export type { PublishedSchema } from './schemasFromSource';

/**
 * Hosts a built web app on Saddle's own domain, at a readable, STABLE URL:
 *
 *   https://app.saddle-sites.com/site/<app-name>
 *
 * rather than the raw storage URL the upload endpoint hands back
 * (`…s3….amazonaws.com/apps/<id>/uploads/general/<nanoid>.html`). This is the product
 * promise — build and deploy in one place — and it also fixes URL churn: the upload
 * endpoint mints a fresh key per publish, so a direct link changed every time an app was
 * re-published, while the slug stays put and the document behind it is overwritten.
 *
 * Reuses the publish-to-website plumbing wholesale: `assignSlug` already gives every
 * published app a unique, name-based slug (suffixing -2, -3 only on a real collision),
 * CloudFront already fronts the domain, and the site handler already resolves slug →
 * appId. The only server change was storing the finished document and serving it.
 *
 * EVERY FAILURE RETURNS null RATHER THAN THROWING. Hosting is an upgrade applied after the
 * app is already built, saved and published to its direct URL — if the catalog write, the
 * network or the deploy is not there, the app must keep the working link it already has.
 */

/**
 * The collections a generated app posts to, with the fields it actually sends, read from
 * the same AST the gate walks.
 *
 * The field names matter as much as the collection: the server rejects any key it has not
 * been told about, so an entity declared with no fields refuses EVERY submission — which
 * is what shipped until a live test posted a real signup and got back
 * "Validation failed: Unknown field: name, email, platform". Types are left as strings
 * because only explicit `validation` rules are enforced server-side, never the declared
 * type, so a plain string field accepts any JSON value.
 */
/** The field the server stamps with the signed-in user's id. Declared alongside the app's
 *  own fields so an update that echoes it back is not rejected as unknown. */


/**
 * Turns a relative token in a starting row into a real date, at publish time.
 *
 * Starting rows have to be literals so the publisher can read them, and a literal cannot
 * call new Date(). Hardcoding "12 September" instead means a supper club built in November
 * ships with dates that have already passed — which is the "seeded dates must be computed
 * from new Date()" rule the store seeds already follow, reappearing in a place where the
 * usual answer is impossible.
 *
 * So a row writes '+3d 18:30' and this resolves it against the moment of publish. Anything
 * that is not a token is passed through untouched.
 */
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;


export function resolveRelativeDates(row: Record<string, unknown>, now = new Date()): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row)) {
    if (typeof value !== 'string') {
      out[key] = value;
      continue;
    }
    const m = DATE_TOKEN.exec(value.trim());
    if (!m) {
      out[key] = value;
      continue;
    }
    const amount = Number(m[1]);
    const unit = m[2].toLowerCase();
    const when = new Date(now.getTime());
    if (unit === 'h') when.setHours(when.getHours() + amount);
    else when.setDate(when.getDate() + amount * (unit === 'w' ? 7 : 1));
    // A named day moves forward to the next such day from the offset, today included, so
    // '+2w fri' is the Friday of that week rather than a day in the past.
    if (m[3] !== undefined) {
      const target = DAYS.indexOf(m[3].toLowerCase() as (typeof DAYS)[number]);
      when.setDate(when.getDate() + ((target - when.getDay() + 7) % 7));
    }
    if (m[4] !== undefined) {
      when.setHours(Number(m[4]), Number(m[5]), 0, 0);
    }
    out[key] = when.toISOString();
  }

  return out;
}

/**
 * Writes an app's starting rows into its owner-managed collections, once.
 *
 * Only into an EMPTY collection. A republish must never duplicate the owner's content, and
 * must never overwrite an edit they made since — by the second publish those rows are the
 * owner's data, not the model's suggestion.
 *
 * Best effort throughout. The app is already published and working by this point; failing
 * the whole hosting step because a seed row was rejected would take away a link that
 * exists, to fix a screen that is merely empty.
 */
export async function seedOwnerContent(
  appId: string,
  source: string,
  client: { listRecords: (entity: string, opts?: { limit?: number }) => Promise<{ items: unknown[] }>; createRecord: (entity: string, data: Record<string, unknown>) => Promise<unknown> },
): Promise<{ seeded: Record<string, number> }> {
  const seeded: Record<string, number> = {};
  let starting: Record<string, Record<string, unknown>[]> = {};
  try {
    starting = listStartingRows(source);
  } catch {
    return { seeded };
  }

  for (const [entity, rows] of Object.entries(starting)) {
    if (!rows.length) continue;
    try {
      const existing = await client.listRecords(entity, { limit: 1 });
      if ((existing?.items?.length ?? 0) > 0) continue; // the owner's content already
      let written = 0;
      for (const row of rows) {
        try {
          await client.createRecord(entity, resolveRelativeDates(row));
          written += 1;
        } catch {
          /* one bad row must not stop the rest */
        }
      }
      if (written) seeded[entity] = written;
    } catch {
      /* cannot read the collection — leave it alone rather than risk duplicating */
    }
  }

  return { seeded };
}

export async function hostOnSaddle(project: Project, html: string): Promise<{ url: string; appId: string } | null> {
  try {
    const [{ useBackendStore }, { buildPublishInput }] = await Promise.all([
      import('@/store/backendStore'),
      import('@/utils/publishInput'),
    ]);
    const platform = useBackendStore.getState().platform;
    if (!platform) return null;

    // Declare a write-only collection for every S.submit target. `fields: []` imposes no
    // validation — the app decides what a signup row looks like — while `webBookable`
    // grants anonymous create and denies anonymous reads, so visitors can join a waitlist
    // but cannot read the list.
    // The plan's rules ride along with the schema — without this the capacity the user
    // chose in the wizard never reaches the server, and every limit is decorative.
    const source = project.definition.source ?? '';
    const plan = (project.definition as {
      plan?: { rules?: PlanRules | null; manages?: { collection: string; fields: string[] }[] };
    }).plan;
    const schemas = withCounts(
      withManaged(withAppRules(publishedSchemas(source), plan?.rules), plan?.manages),
      source,
    );

    // REUSE the app this project is already hosted as. `POST /apps` always mints a new id,
    // so publishing again created a second catalog app, which collided on the slug and got
    // suffixed — the published URL changed to …-2 on every re-publish, which is precisely
    // the churn this hosting was built to end. An app that already has an id only needs
    // its document replaced.
    //
    // On first publish: the server refuses to web-publish a `personal` app. For a web app
    // that distinction does not apply the way it does to an engine app with end-user
    // accounts — the app IS a link — so the hosted copy is marked sharable. The local
    // project keeps whatever the wizard recorded.
    // Default to 'users': AI works for anyone with a (free) account, so every call is
    // traceable to a person and a stranger cannot silently spend the creator's credits.
    const aiAccess = project.definition.aiAccess ?? 'users';
    // The owner's ceiling on what this page may spend. Sent every publish so a change
    // takes effect without the app being rebuilt.
    const aiLimits = {
      ...(project.definition.aiMonthlyBudget ? { aiMonthlyBudget: project.definition.aiMonthlyBudget } : {}),
      ...(project.definition.aiDailyPerPerson ? { aiDailyPerPerson: project.definition.aiDailyPerPerson } : {}),
    };

    const create = async (): Promise<string | null> => {
      const listing = await platform.publishApp(
        buildPublishInput(
          { ...project, definition: { ...project.definition, kind: 'sharable', schemas } },
          {},
        ),
      );
      return listing?.appId ?? null;
    };

    let appId = project.definition.hostedAppId ?? (await create());
    if (!appId) return null;

    let result;
    try {
      result = await platform.publishToWeb(appId, { visibility: 'public', html, schemas, aiAccess, ...aiLimits });
    } catch (e) {
      // The app this project points at can be gone — deleted from the catalog, or wiped
      // with the account. Reusing a dead id used to fail the whole hosting step, and the
      // app quietly dropped back to its raw storage URL, losing the saddle link it had.
      // Publish it as a new app instead; the slug is reclaimed if it was freed.
      if (!project.definition.hostedAppId) throw e;
      const fresh = await create();
      if (!fresh) return null;
      appId = fresh;
      result = await platform.publishToWeb(appId, { visibility: 'public', html, schemas, aiAccess, ...aiLimits });
    }

    // `htmlHosted` is the server telling us it actually stored the document. Without it we
    // are talking to an older deploy whose site handler would try to server-render this
    // app's EMPTY AppDefinition — a blank page at a pretty URL, which is worse than the
    // direct link that already works. Only trust the slug URL when the server confirms.
    if (!result?.htmlHosted || !result.siteUrl) return null;

    // The owner opens their own link to a working app rather than an empty state. After
    // this first write the rows are theirs, so it never happens again for this collection.
    try {
      await seedOwnerContent(appId, project.definition.source ?? '', useBackendStore.getState().getClient(appId));
    } catch {
      /* the app is live either way */
    }

    return { url: result.siteUrl, appId };
  } catch {
    return null;
  }
}


export type AddressRefusal = 'not-signed-in' | 'no-backend' | 'not-published' | 'taken' | 'invalid' | 'failed';

export class AddressError extends Error {
  readonly reason: AddressRefusal;
  constructor(reason: AddressRefusal) {
    super(ADDRESS_MESSAGES[reason]);
    this.name = 'AddressError';
    this.reason = reason;
  }
}

const ADDRESS_MESSAGES: Record<AddressRefusal, string> = {
  'not-signed-in': 'Sign in to change this address.',
  'no-backend': 'No backend is configured, so there is nowhere to publish.',
  'not-published': 'Publish this app first — it needs an address before it can move.',
  taken: 'That address is already taken. Try another.',
  invalid: 'Use 2 to 60 letters or numbers.',
  failed: "That didn't go through. Try again.",
};

export interface AddressChange {
  slug: string;
  siteUrl?: string;
  /** Earlier addresses that still open this app. */
  keptWorking: string[];
}

/**
 * Move a published app to an address its owner chose.
 *
 * The old address keeps working: someone has that link, and a rename they never hear
 * about is no reason for it to stop opening.
 */
export async function changeSiteAddress(
  hostedAppId: string | undefined,
  desired: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AddressChange> {
  const { useDeploymentStore } = await import('@/store/deploymentStore');
  const { CognitoService } = await import('@/auth/CognitoService');
  const baseUrl = (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (!baseUrl) throw new AddressError('no-backend');
  if (!hostedAppId) throw new AddressError('not-published');
  const token = CognitoService.getAccessTokenSync();
  if (!token) throw new AddressError('not-signed-in');

  let res: Response;
  try {
    res = await fetchImpl(`${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/web/address`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug: desired }),
    });
  } catch {
    throw new AddressError('failed');
  }
  if (res.status === 409) throw new AddressError('taken');
  if (res.status === 400) throw new AddressError('invalid');
  if (!res.ok) throw new AddressError('failed');

  const body = (await res.json()) as { slug?: string; siteUrl?: string; keptWorking?: string[] };
  if (!body.slug) throw new AddressError('failed');
  return { slug: body.slug, siteUrl: body.siteUrl, keptWorking: body.keptWorking ?? [] };
}
